<template>
  <div
    class="auth-view"
    data-view="reset"
  >
    <!-- Set-new-password form -->
    <template v-if="stage === 'form'">
      <h2>
        {{ t('reset_title_prefix') }}<span class="grad">{{ t('reset_title_grad') }}</span
        >{{ t('reset_title_suffix') }}
      </h2>
      <p class="sub">
        {{ t('reset_sub_prefix') }}<strong>{{ email }}</strong>{{ t('reset_sub_suffix') }}
      </p>

      <div class="signup-field">
        <label
          class="field-label"
          id="reset-pw-label"
          >{{ t('lbl_reset_password') }}</label
        >
        <div class="pw-wrap">
          <input
            id="reset-pw"
            v-model="password"
            :type="showPw ? 'text' : 'password'"
            class="signup-input"
            :class="{ error: errors.password }"
            aria-labelledby="reset-pw-label"
            :placeholder="t('ph_password')"
            autocomplete="new-password"
            @keyup.enter="focusConfirm"
          />
          <button
            type="button"
            class="pw-toggle"
            :aria-label="t('lbl_reset_password')"
            @click="showPw = !showPw"
          >
            <icon-eye :size="18" />
          </button>
        </div>
        <div
          class="strength"
          :class="'s' + pwStrength"
          aria-hidden="true"
        >
          <span class="bar"></span><span class="bar"></span><span class="bar"></span
          ><span class="bar"></span>
        </div>
        <div
          v-if="errors.password"
          class="err"
        >
          <icon-info-circle
            :size="13"
            :stroke-width="2.4"
          />
          <span>{{ errors.password }}</span>
        </div>
        <div
          v-else
          class="field-note"
        >
          {{ t('helper_password') }}
        </div>
      </div>

      <div class="signup-field">
        <label
          class="field-label"
          id="reset-pw2-label"
          >{{ t('lbl_reset_password_confirm') }}</label
        >
        <div class="pw-wrap">
          <input
            id="reset-pw2"
            ref="confirm"
            v-model="passwordConfirmation"
            :type="showPw2 ? 'text' : 'password'"
            class="signup-input"
            :class="{ error: errors.password_confirmation }"
            aria-labelledby="reset-pw2-label"
            :placeholder="t('ph_reset_confirm')"
            autocomplete="new-password"
            @keyup.enter="submit"
          />
          <button
            type="button"
            class="pw-toggle"
            :aria-label="t('lbl_reset_password_confirm')"
            @click="showPw2 = !showPw2"
          >
            <icon-eye :size="18" />
          </button>
        </div>
        <div
          v-if="errors.password_confirmation"
          class="err"
        >
          <icon-info-circle
            :size="13"
            :stroke-width="2.4"
          />
          <span>{{ errors.password_confirmation }}</span>
        </div>
      </div>

      <div
        v-if="submitError"
        class="submit-err show"
      >
        <icon-info-circle
          :size="16"
          :stroke-width="2.4"
        />
        <span>{{ submitError }}</span>
      </div>

      <button
        type="button"
        class="sb-next wide"
        :disabled="submitting"
        @click="submit"
      >
        <template v-if="submitting">
          <span
            class="spinner"
            aria-hidden="true"
          ></span>
          {{ t('btn_loading') }}
        </template>
        <template v-else>{{ t('reset_btn') }} <span>→</span></template>
      </button>
    </template>

    <!-- Success: password changed -->
    <div
      v-else-if="stage === 'done'"
      class="auth-confirm"
    >
      <div class="icon success">
        <icon-check-circle
          :size="46"
          :stroke-width="2.3"
        />
      </div>
      <h2>{{ t('reset_done_title') }}</h2>
      <p>{{ t('reset_done_body') }}</p>
      <button
        type="button"
        class="sb-next wide"
        @click="goToLogin"
      >
        {{ t('reset_done_btn') }}
      </button>
    </div>

    <!-- Invalid / expired link -->
    <div
      v-else
      class="auth-confirm"
    >
      <div class="icon invalid">
        <icon-info-circle
          :size="44"
          :stroke-width="2.3"
        />
      </div>
      <h2>{{ t('reset_invalid_title') }}</h2>
      <p>{{ t('reset_invalid_body') }}</p>
      <button
        type="button"
        class="sb-next wide"
        @click="$emit('switch-mode', 'forgot')"
      >
        {{ t('reset_invalid_btn') }}
      </button>
    </div>
  </div>
</template>

<script>
import { i18nMixin, refreshCsrfToken, passwordStrengthScore, isPasswordValid } from './helpers';
import IconInfoCircle from '../icons/IconInfoCircle.vue';
import IconEye from '../icons/IconEye.vue';
import IconCheckCircle from '../icons/IconCheckCircle.vue';

// The reset link is /password/reset/{token}/{login} (see routes/web.php). We read
// the token + login (an e-mail for the link flow) straight off the URL, mirroring
// the legacy ResetPasswordEmailCard but with a defensive parse instead of split().
const RESET_PATH_RE = /\/password\/reset\/([^/]+)\/([^/]+)\/?$/;

function parseResetUrl() {
  const m = (window.location.pathname || '').match(RESET_PATH_RE);
  if (!m) {
    return { token: '', email: '' };
  }
  let email = m[2];
  try {
    email = decodeURIComponent(email);
  } catch (e) {
    // Malformed encoding — keep the raw segment so the backend can still 422 it.
  }
  return { token: m[1], email };
}

export default {
  name: 'ResetPasswordForm',
  mixins: [i18nMixin],
  components: { IconInfoCircle, IconEye, IconCheckCircle },
  data() {
    const { token, email } = parseResetUrl();
    return {
      token,
      email,
      password: '',
      passwordConfirmation: '',
      showPw: false,
      showPw2: false,
      submitting: false,
      submitError: '',
      errors: {},
      // No token/login in the URL means the link is malformed — skip straight to
      // the invalid view rather than letting the user submit a doomed request.
      stage: token && email ? 'form' : 'invalid',
    };
  },
  computed: {
    pwStrength() {
      return passwordStrengthScore(this.password);
    },
  },
  methods: {
    focusConfirm() {
      const el = this.$refs.confirm;
      if (el && typeof el.focus === 'function') {
        el.focus();
      }
    },
    validate() {
      this.errors = {};
      if (!isPasswordValid(this.password)) {
        this.errors = { password: this.t('err_password_min') };
        return false;
      }
      if (this.password !== this.passwordConfirmation) {
        this.errors = { password_confirmation: this.t('reset_match_err') };
        return false;
      }
      return true;
    },
    async submit() {
      this.submitError = '';
      if (this.submitting || !this.validate()) {
        return;
      }
      this.submitting = true;
      this.$emit('submitting', true);
      await refreshCsrfToken();
      try {
        // Field shape matches ResetEmailPasswordTrait::resetByLink validation
        // (token + email + password|confirmed|min:8). The backend logs the user
        // in on success, but we still route them through the login form below to
        // match the prepared copy (reset_done_* / login_after_reset_banner).
        await axios.post(zRoute('password.update.email'), {
          token: this.token,
          email: this.email,
          password: this.password,
          password_confirmation: this.passwordConfirmation,
        });
        this.stage = 'done';
      } catch (err) {
        const data = err && err.response && err.response.data;
        const fieldErrors = (data && data.errors) || {};
        if (fieldErrors.email) {
          // The broker reports an expired/unknown token under `email` — show the
          // dedicated invalid-link view so the user can request a fresh link.
          this.stage = 'invalid';
        } else if (fieldErrors.password) {
          this.errors = {
            password: Array.isArray(fieldErrors.password)
              ? fieldErrors.password[0]
              : fieldErrors.password,
          };
        } else {
          this.submitError = (data && data.message) || this.t('reset_err');
        }
      } finally {
        this.submitting = false;
        this.$emit('submitting', false);
      }
    },
    goToLogin() {
      this.$emit('switch-mode', 'login', { afterReset: true });
    },
  },
};
</script>
